import { useMemo } from "react";
import { Link, useNavigate, useRouteLoaderData } from "react-router-dom";
import UserInfo from "src/components/common/userInfo";
import { useAuthStore } from "src/components/main/authStore";
import {
    buildWeekNumArray,
    changeWeek,
    getDateEndFromWeekNum,
    getDatefromWeekNum,
    MyDate,
    myAxios,
    outsideCordinators,
    standbyStartDate,
    weekDays,
    weeknum,
} from "src/components/common/functions";

type StandbyWeekData = {
    data?: any[];
    status?: string;
    submitTime?: string;
    id?: string | null;
    billableId?: string | null;
    otherNotes?: string;
    region?: string;
    week?: string;
};

type LoaderData = {
    week: string;
    form: StandbyWeekData;
    prevForm?: StandbyWeekData;
};

export default function Standby() {
    const { week, form: loadedForm } = (useRouteLoaderData("StandbyTimeCard") as LoaderData | undefined) ?? { week: "", form: {} };
    const navigate = useNavigate();
    const user = useAuthStore((state) => state.user);
    const weekForm = { ...loadedForm, week };
    const weeks = useMemo(() => buildWeekNumArray(
        user?.hireDate && user.hireDate > standbyStartDate ? user.hireDate : standbyStartDate,
        user?.quitDate && new Date() > new Date(user.quitDate.replace(/-/g, "/")) ? user.quitDate : (new MyDate()).s()
    ).reverse(), [user?.hireDate, user?.quitDate]);

    const today = new MyDate();
    const late = ((weeknum(today)) > changeWeek(week, 1)) || (
        (weeknum(today)) > week &&
        (((today.getDay() === 1) && today.getHours() >= 8) || (today.getDay() > 1 || today.getDay() === 0))
    );
    const editable = weekForm.status !== "Submitted" || !late;
    const dayRows = useMemo(() => (weekForm.data ?? []).map((day, index) => {
        const jobs = Array.isArray(day) ? day.slice(1).filter((job: any) => job?.category) : [];
        return {
            index,
            label: weekDays[index],
            date: getDatefromWeekNum(week, index) || "",
            entries: jobs.length,
            categories: jobs.map((job: any) => job.category).join(", "),
        };
    }), [week, weekForm.data]);
    const totalEntries = useMemo(
        () => dayRows.reduce((sum, row) => sum + row.entries, 0),
        [dayRows]
    );

    const openExcel = async (fileId: string, fileName: string) => {
        const formData = new FormData();
        formData.append("fileId", fileId);
        const res = await myAxios({ fileName: "outside/standby/getExcel.php", formData });
        if (!res?.data) return;
        const url = typeof res.data === "string" ? res.data : "";
        if (!url) return;
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        anchor.target = "_blank";
        anchor.rel = "noopener noreferrer";
        anchor.click();
    };

    return (
        <div>
            <nav className = "d-flex align-items-center justify-content-between flex-wrap gap-3 mt-3">
                {weeks.length ? (
                    <span>
                        <span className = "mx-2">Week</span>
                        <select
                            className = "form-control inline"
                            value = {week}
                            onChange = {e => navigate(`/StandbyTimeCard/${e.currentTarget.value}`)}
                        >
                            {weeks.map((value, index) => <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                        </select>
                    </span>
                ) : null}
            </nav>
            <div className = "px-3">
                <div className = "my-3 d-flex flex-wrap gap-3">
                    <div className = "border rounded p-3 bg-white" style = {{ minWidth: 220 }}>
                        <div className = "small text-muted fw-bold">Week</div>
                        <div className = "h5 mb-0">{getDateEndFromWeekNum(week)}</div>
                    </div>
                    <div className = "border rounded p-3 bg-white" style = {{ minWidth: 180 }}>
                        <div className = "small text-muted fw-bold">Status</div>
                        <div className = "h5 mb-0">{weekForm.status || "Created"}</div>
                    </div>
                    <div className = "border rounded p-3 bg-white" style = {{ minWidth: 180 }}>
                        <div className = "small text-muted fw-bold">Region</div>
                        <div className = "h5 mb-0">{weekForm.region || "N/A"}</div>
                    </div>
                    <div className = "border rounded p-3 bg-white" style = {{ minWidth: 180 }}>
                        <div className = "small text-muted fw-bold">Entries</div>
                        <div className = "h5 mb-0">{totalEntries}</div>
                    </div>
                </div>

                {weekForm.submitTime ? (
                    <div className = "mb-3 text-success fw-bold">Submitted on {weekForm.submitTime}</div>
                ) : null}

                {!editable ? (
                    <div className = "mb-3 text-danger fw-bold">
                        Submission window is closed for selected week. Please contact{" "}
                        {(outsideCordinators as Array<{ name: string; email?: string; id?: string }>).map((outsideCordinator, index) => (
                            <span key = {index}>
                                <UserInfo
                                    name = {outsideCordinator.name}
                                    id = {outsideCordinator.id || outsideCordinator.email || outsideCordinator.name}
                                />
                                {index < outsideCordinators.length - 1 ? ", " : ""}
                            </span>
                        ))}
                        {" "}for any edits.
                    </div>
                ) : null}

                {(weekForm.id || weekForm.billableId) ? (
                    <div className = "mb-3 d-flex flex-wrap gap-2">
                        {weekForm.id ? (
                            <button
                                type = "button"
                                className = "btn btn-primary"
                                onClick = {() => openExcel(String(weekForm.id), `${week}.xlsx`)}
                            >
                                Payroll
                            </button>
                        ) : null}
                        {weekForm.billableId ? (
                            <button
                                type = "button"
                                className = "btn btn-success"
                                onClick = {() => openExcel(String(weekForm.billableId), `${week} billable.xlsx`)}
                            >
                                Billable
                            </button>
                        ) : null}
                    </div>
                ) : null}

                <div className = "table-responsive">
                    <table className = "table">
                        <thead className = "noTableTopBorder">
                            <tr>
                                <th>Day</th>
                                <th>Date</th>
                                <th>Entries</th>
                                <th>Categories</th>
                            </tr>
                        </thead>
                        <tbody>
                            {dayRows.map((row) => (
                                <tr key = {row.index}>
                                    <td>{row.label}</td>
                                    <td>{row.date}</td>
                                    <td>{row.entries}</td>
                                    <td>{row.categories || "None"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className = "mb-3">
                    <div className = "small text-muted fw-bold mb-1">Other Notes</div>
                    <div className = "border rounded p-3 bg-white" style = {{ whiteSpace: "pre-wrap" }}>
                        {weekForm.otherNotes || "None"}
                    </div>
                </div>
                {editable && 
                <div className="mb-3">
                    <Link className = "btn btn-outline-primary" to = "Edit">Edit</Link>
                </div>}
            </div>
        </div>
    );
}
